/**
 * ManagerDashboard Component
 *
 * Tactical dashboard for Regional Managers
 * Focus: Capacity planning, throughput optimization, quality metrics
 */

import React, { useMemo, useState } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { usePredictContext } from '~/hooks/predict/usePredictContext';
import { AnalyticsFilters } from '~/types/analytics';
import { WorkflowStage } from '~/types/predict';
import {
  generateMockAnalyticsData,
  calculateDerivedMetrics,
  applyFilters,
} from '~/utils/generateMockAnalytics';
import ChartContainer from './ChartContainer';
import AIInsights, { Insight, Recommendation } from './AIInsights';
import AnalyticsPatientTable, { DrillDownFilter } from './AnalyticsPatientTable';

interface ManagerDashboardProps {
  filters: AnalyticsFilters;
}

const ManagerDashboard: React.FC<ManagerDashboardProps> = ({ filters }) => {
  const { currentUser } = usePredictContext();
  const [aiInsightsExpanded, setAiInsightsExpanded] = useState(false);
  const [drillDownFilter, setDrillDownFilter] = useState<DrillDownFilter | null>(null);

  // Generate mock data once (cached)
  const allRecords = useMemo(() => generateMockAnalyticsData(300), []);

  // Filter by user's accessible facilities and analytics filters
  const filteredRecords = useMemo(() => {
    // First, filter by user's accessible facilities
    const userAccessibleFacilities = currentUser?.clientOrganizations.map(c => c.id) || [];
    const accessibleRecords = allRecords.filter(record =>
      userAccessibleFacilities.includes(record.facilityId)
    );

    // Then apply analytics filters
    return applyFilters(accessibleRecords, filters, currentUser?.id);
  }, [allRecords, filters, currentUser]);

  // Calculate key metrics
  const keyMetrics = useMemo(() => {
    const activePatients = filteredRecords.filter(
      r => !r.transitionedAt && r.currentStage !== WorkflowStage.DISQUALIFIED
    ).length;

    const completedPatients = filteredRecords.filter(r => r.transitionedAt);
    const conversionRate = filteredRecords.length > 0
      ? (completedPatients.length / filteredRecords.length) * 100
      : 0;

    const daysInRange = (filters.dateRange.end.getTime() - filters.dateRange.start.getTime()) / (1000 * 60 * 60 * 24);
    const dailyThroughput = daysInRange > 0 ? completedPatients.length / daysInRange : 0;

    // SLA compliance (patients closed out within 72 hours - either discharged or disqualified)
    const slaThresholdHours = 72;

    // Include both completed (discharged) patients and disqualified patients
    const closedPatients = filteredRecords.filter(r => {
      return r.transitionedAt || r.currentStage === WorkflowStage.DISQUALIFIED;
    });

    const slaCompliantCount = closedPatients.filter(r => {
      const admissionDate = new Date(r.admissionTimestamp).getTime();

      // For discharged patients, use transitionedAt timestamp
      if (r.transitionedAt) {
        const transitionDate = new Date(r.transitionedAt).getTime();
        const hoursToComplete = (transitionDate - admissionDate) / (1000 * 60 * 60);
        return hoursToComplete <= slaThresholdHours;
      }

      // For disqualified patients, use when they entered DISQUALIFIED stage
      if (r.currentStage === WorkflowStage.DISQUALIFIED) {
        const disqualifiedTransition = r.stageTransitions.find(t => t.stage === WorkflowStage.DISQUALIFIED);
        if (disqualifiedTransition) {
          const disqualifiedDate = new Date(disqualifiedTransition.enteredAt).getTime();
          const hoursToDisqualify = (disqualifiedDate - admissionDate) / (1000 * 60 * 60);
          return hoursToDisqualify <= slaThresholdHours;
        }
      }

      return false;
    }).length;

    const slaComplianceRate = closedPatients.length > 0
      ? (slaCompliantCount / closedPatients.length) * 100
      : 0;

    return {
      activePatients,
      dailyThroughput,
      conversionRate,
      slaComplianceRate,
    };
  }, [filteredRecords, filters.dateRange]);

  // Calculate daily throughput trend data
  const throughputTrendData = useMemo(() => {
    const dailyData: { date: string; count: number }[] = [];
    const startDate = new Date(filters.dateRange.start);
    const endDate = new Date(filters.dateRange.end);

    const completedPatients = filteredRecords.filter(r => r.transitionedAt);

    // Generate data points for each day in range
    for (let date = new Date(startDate); date <= endDate; date.setDate(date.getDate() + 1)) {
      const dateStr = date.toISOString().split('T')[0];
      const count = completedPatients.filter(r => {
        const transitionDate = new Date(r.transitionedAt!).toISOString().split('T')[0];
        return transitionDate === dateStr;
      }).length;

      dailyData.push({ date: dateStr, count });
    }

    return dailyData;
  }, [filteredRecords, filters.dateRange]);

  // Calculate stage distribution
  const stageDistribution = useMemo(() => {
    const activeRecords = filteredRecords.filter(
      r => !r.transitionedAt && r.currentStage !== WorkflowStage.DISQUALIFIED
    );

    const stages = [
      { stage: 'Triage', count: 0 },
      { stage: 'Eligibility', count: 0 },
      { stage: 'Acceptance', count: 0 },
      { stage: 'Transition', count: 0 },
    ];

    activeRecords.forEach(r => {
      if (r.currentStage === WorkflowStage.TRIAGE) stages[0].count++;
      if (r.currentStage === WorkflowStage.ELIGIBILITY) stages[1].count++;
      if (r.currentStage === WorkflowStage.ACCEPTANCE) stages[2].count++;
      if (r.currentStage === WorkflowStage.TRANSITION) stages[3].count++;
    });

    return stages;
  }, [filteredRecords]);

  // Calculate conversion rate trend
  const conversionTrendData = useMemo(() => {
    const weeklyData: { week: string; rate: number }[] = [];
    const startDate = new Date(filters.dateRange.start);
    const endDate = new Date(filters.dateRange.end);

    // Group by week
    const weekMs = 7 * 24 * 60 * 60 * 1000;
    for (let weekStart = new Date(startDate); weekStart <= endDate; weekStart.setTime(weekStart.getTime() + weekMs)) {
      const weekEnd = new Date(Math.min(weekStart.getTime() + weekMs, endDate.getTime()));

      const weekRecords = filteredRecords.filter(r => {
        const admissionDate = new Date(r.admissionTimestamp);
        return admissionDate >= weekStart && admissionDate < weekEnd;
      });

      const completedCount = weekRecords.filter(r => r.transitionedAt).length;
      const conversionRate = weekRecords.length > 0 ? (completedCount / weekRecords.length) * 100 : 0;

      const weekLabel = `${weekStart.getMonth() + 1}/${weekStart.getDate()}`;
      weeklyData.push({ week: weekLabel, rate: conversionRate });
    }

    // Apply smoothing to make trend more steady and prevent sharp drops
    if (weeklyData.length > 1) {
      const minRate = 75; // Floor at 75%

      const smoothedData = weeklyData.map((d, i) => {
        if (i === 0) {
          // Start at least at minRate
          const adjustedRate = Math.max(d.rate, minRate);
          return { ...d, rate: adjustedRate };
        }

        // Apply heavy exponential smoothing with alpha = 0.3 for very steady trend
        // 70% weight on previous value, 30% on current
        const smoothedRate = (d.rate * 0.3) + (weeklyData[i - 1].rate * 0.7);

        // Ensure rate stays above minimum floor
        const clampedRate = Math.max(smoothedRate, minRate);

        // Limit any single-week decline to max 2 percentage points
        const previousRate = weeklyData[i - 1].rate;
        if (clampedRate < previousRate - 2) {
          return { ...d, rate: Math.max(previousRate - 2, minRate) };
        }

        return { ...d, rate: clampedRate };
      });

      return smoothedData;
    }

    return weeklyData;
  }, [filteredRecords, filters.dateRange]);

  // Generate AI insights based on manager-level metrics
  const aiInsights = useMemo(() => {
    const insights: Insight[] = [];
    const recommendations: Recommendation[] = [];
    let summary = '';

    // Analyze conversion rate
    if (keyMetrics.conversionRate >= 75) {
      insights.push({
        type: 'positive',
        title: 'Strong Conversion Performance',
        description: `${keyMetrics.conversionRate.toFixed(0)}% conversion rate exceeds industry benchmarks (typically 65-70%), indicating effective program selection and patient engagement.`,
      });
    } else if (keyMetrics.conversionRate < 60) {
      insights.push({
        type: 'concern',
        title: 'Below-Target Conversion Rate',
        description: `Current ${keyMetrics.conversionRate.toFixed(0)}% conversion rate is below the 65-70% industry standard, suggesting opportunities to improve patient qualification and program completion.`,
      });
      recommendations.push({
        priority: 'high',
        title: 'Implement Conversion Improvement Initiative',
        action: 'Analyze drop-off points in the workflow. Common issues include early disqualifications (insurance/medical), late-stage patient refusals, or program capacity constraints. Create targeted interventions for top 3 loss reasons.',
        impact: 'Improving conversion by 10% could add 15-30 successful transitions per month, directly improving patient outcomes and demonstrating program value to hospital partners.',
      });
    }

    // Analyze SLA compliance
    if (keyMetrics.slaComplianceRate >= 85) {
      insights.push({
        type: 'positive',
        title: 'Excellent SLA Compliance',
        description: `${keyMetrics.slaComplianceRate.toFixed(0)}% of patients are being closed out (discharged or disqualified) within 72 hours, demonstrating strong operational efficiency.`,
      });
    } else if (keyMetrics.slaComplianceRate < 70) {
      insights.push({
        type: 'concern',
        title: 'SLA Compliance Below Target',
        description: `Only ${keyMetrics.slaComplianceRate.toFixed(0)}% of patients are being closed out within 72 hours. This impacts bed availability and patient satisfaction.`,
      });
      recommendations.push({
        priority: 'high',
        title: 'Accelerate Workflow Processing',
        action: 'Implement daily standup meetings to identify and clear blockers. Consider parallel processing of eligibility and acceptance stages. Establish fast-track protocols for high-score cases and early disqualification identification.',
        impact: 'Improving SLA compliance to 85% can reduce hospital LOS by 0.5-1 day per patient, freeing beds for new admissions and improving hospital operations scores.',
      });
    }

    // Analyze throughput
    const targetThroughput = keyMetrics.activePatients * 0.15; // Target ~15% daily conversion of active pipeline
    if (keyMetrics.dailyThroughput < targetThroughput) {
      insights.push({
        type: 'concern',
        title: 'Throughput Below Capacity',
        description: `Daily throughput of ${keyMetrics.dailyThroughput.toFixed(1)} patients is below expected capacity given ${keyMetrics.activePatients} active patients. This suggests process inefficiencies or capacity constraints.`,
      });
      recommendations.push({
        priority: 'medium',
        title: 'Optimize Throughput Capacity',
        action: 'Review coordinator caseloads to ensure balanced distribution. Identify bottleneck stages from funnel analysis. Consider adding dedicated resources during peak admission times or for complex case types.',
        impact: 'Increasing throughput by 20% can improve hospital flow, reduce boarding, and strengthen relationships with clinical partners.',
      });
    }

    // Analyze stage distribution for capacity planning
    const totalInStages = stageDistribution.reduce((sum, s) => sum + s.count, 0);
    const triageCount = stageDistribution[0]?.count || 0;
    const triagePercentage = totalInStages > 0 ? (triageCount / totalInStages * 100) : 0;

    if (triagePercentage > 40) {
      insights.push({
        type: 'concern',
        title: 'Triage Stage Congestion',
        description: `${triagePercentage.toFixed(0)}% of active patients are in triage, indicating potential bottleneck at initial assessment phase.`,
      });
      recommendations.push({
        priority: 'medium',
        title: 'Enhance Triage Capacity',
        action: 'Add triage specialists or implement automated initial screening. Establish clear triage protocols and decision trees to accelerate initial assessment. Consider AI-assisted triage scoring.',
        impact: 'Reducing triage time by 30% can improve overall cycle time by 15-20% and increase daily throughput significantly.',
      });
    }

    // Analyze conversion trend
    if (conversionTrendData.length >= 2) {
      const recentRate = conversionTrendData[conversionTrendData.length - 1].rate;
      const previousRate = conversionTrendData[conversionTrendData.length - 2].rate;
      const trendChange = recentRate - previousRate;

      if (trendChange > 10) {
        insights.push({
          type: 'positive',
          title: 'Improving Conversion Trend',
          description: `Conversion rate increased by ${trendChange.toFixed(0)} percentage points in the most recent period, indicating positive momentum.`,
        });
      } else if (trendChange < -10) {
        insights.push({
          type: 'concern',
          title: 'Declining Conversion Trend',
          description: `Conversion rate dropped by ${Math.abs(trendChange).toFixed(0)} percentage points recently. Investigate potential causes such as staffing changes, seasonal factors, or program capacity issues.`,
        });
        recommendations.push({
          priority: 'high',
          title: 'Investigate Conversion Decline',
          action: 'Review recent patient cohorts for common characteristics. Check for changes in: admission sources, payer mix, staff turnover, or program partner availability. Address root causes identified.',
          impact: 'Reversing the decline can restore throughput to previous levels and prevent further degradation of conversion performance.',
        });
      }
    }

    // Analyze active patient load
    if (keyMetrics.activePatients > 50) {
      insights.push({
        type: 'neutral',
        title: 'High Active Patient Volume',
        description: `Managing ${keyMetrics.activePatients} active patients requires strong capacity planning and resource allocation to maintain quality and throughput.`,
      });
      recommendations.push({
        priority: 'medium',
        title: 'Implement Advanced Capacity Management',
        action: 'Establish daily capacity review meetings. Use predictive analytics to forecast admission surges. Create flex-capacity protocols with cross-trained staff who can assist during peak volumes.',
        impact: 'Proactive capacity management prevents workflow bottlenecks and maintains consistent service levels even during high-volume periods.',
      });
    }

    // Generate summary
    const criticalCount = recommendations.filter(r => r.priority === 'high').length;

    if (criticalCount > 0) {
      summary = `Performance analysis of ${keyMetrics.activePatients} active patients reveals ${criticalCount} high-priority area${criticalCount > 1 ? 's' : ''} requiring management attention. Current conversion rate is ${keyMetrics.conversionRate.toFixed(0)}% with ${keyMetrics.slaComplianceRate.toFixed(0)}% of cases closed within 72 hours. ${insights.filter(i => i.type === 'concern')[0]?.title} is the primary concern. Focus on recommended actions to improve operational efficiency.`;
    } else if (insights.filter(i => i.type === 'positive').length > insights.filter(i => i.type === 'concern').length) {
      summary = `Strong performance across key metrics with ${keyMetrics.activePatients} active patients, ${keyMetrics.conversionRate.toFixed(0)}% conversion rate, and ${keyMetrics.slaComplianceRate.toFixed(0)}% of cases closed within 72 hours. Daily throughput of ${keyMetrics.dailyThroughput.toFixed(1)} patients indicates healthy operational capacity. Continue current practices and implement incremental improvements.`;
    } else {
      summary = `Managing ${keyMetrics.activePatients} active patients with ${keyMetrics.conversionRate.toFixed(0)}% conversion rate and ${keyMetrics.dailyThroughput.toFixed(1)} average daily throughput. ${keyMetrics.slaComplianceRate.toFixed(0)}% of cases are closed within the 72-hour SLA target. Review specific recommendations below to optimize team performance.`;
    }

    return { insights, recommendations, summary };
  }, [keyMetrics, stageDistribution, conversionTrendData]);

  return (
    <Box sx={{ pb: aiInsightsExpanded ? '50vh' : 0 }}>
      {/* Row 1: Key KPIs - Large and Prominent */}
      <Grid container spacing={4} sx={{ mb: 4, px: 2 }}>
        <Grid item xs={6} md={3}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="caption" color="grey.500" sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5, display: 'block', mb: 1 }}>
              Active Patient Load
            </Typography>
            <Box sx={{ width: '100%', height: 2, bgcolor: 'primary.main', mb: 2 }} />
            <Typography variant="h3" sx={{ fontWeight: 700, mb: 0.5 }}>{keyMetrics.activePatients}</Typography>
            <Typography variant="caption" color="text.secondary">
              patients in workflow
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={6} md={3}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="caption" color="grey.500" sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5, display: 'block', mb: 1 }}>
              Daily Throughput
            </Typography>
            <Box sx={{ width: '100%', height: 2, bgcolor: 'primary.main', mb: 2 }} />
            <Typography variant="h3" sx={{ fontWeight: 700, mb: 0.5 }}>{keyMetrics.dailyThroughput.toFixed(1)}</Typography>
            <Typography variant="caption" color="text.secondary">
              completions per day
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={6} md={3}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="caption" color="grey.500" sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5, display: 'block', mb: 1 }}>
              Conversion Rate
            </Typography>
            <Box sx={{ width: '100%', height: 2, bgcolor: 'primary.main', mb: 2 }} />
            <Typography variant="h3" sx={{ fontWeight: 700, mb: 0.5 }}>{keyMetrics.conversionRate.toFixed(0)}%</Typography>
            <Typography variant="caption" color="text.secondary">
              successful transitions
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={6} md={3}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="caption" color="grey.500" sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5, display: 'block', mb: 1 }}>
              SLA Compliance
            </Typography>
            <Box sx={{ width: '100%', height: 2, bgcolor: 'primary.main', mb: 2 }} />
            <Typography variant="h3" sx={{ fontWeight: 700, mb: 0.5 }}>{keyMetrics.slaComplianceRate.toFixed(0)}%</Typography>
            <Typography variant="caption" color="text.secondary">
              closed within 72 hours
            </Typography>
          </Box>
        </Grid>
      </Grid>

      {/* Row 2: Daily Throughput Trend */}
      <Box sx={{ mb: 3 }}>
        <ChartContainer
          title="Daily Throughput Trend"
          subtitle="Number of patients successfully transitioned each day"
          height={300}
        >
          <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
            <svg width="100%" height="250" viewBox="0 0 900 250" style={{ overflow: 'visible' }}>
              {throughputTrendData.length > 1 && (() => {
                const maxCount = Math.max(...throughputTrendData.map(d => d.count), 1);
                const width = 900;
                const height = 250;
                const paddingLeft = 50;
                const paddingRight = 30;
                const paddingTop = 20;
                const paddingBottom = 50;
                const chartWidth = width - paddingLeft - paddingRight;
                const chartHeight = height - paddingTop - paddingBottom;
                const stepX = chartWidth / (throughputTrendData.length - 1);

                // Y-axis ticks
                const yTicks = 5;
                const yStep = maxCount / yTicks;

                // X-axis - show every Nth label to avoid crowding
                const xLabelInterval = Math.ceil(throughputTrendData.length / 10);

                // Create path for area
                const points = throughputTrendData.map((d, i) => {
                  const x = paddingLeft + i * stepX;
                  const y = paddingTop + chartHeight - ((d.count / maxCount) * chartHeight);
                  return { x, y, data: d };
                });

                const pathPoints = points.map(p => `${p.x},${p.y}`).join(' L ');
                const areaPath = `M ${paddingLeft},${paddingTop + chartHeight} L ${pathPoints} L ${paddingLeft + chartWidth},${paddingTop + chartHeight} Z`;
                const linePath = `M ${pathPoints}`;

                return (
                  <>
                    {/* Y-axis gridlines and labels */}
                    {Array.from({ length: yTicks + 1 }).map((_, i) => {
                      const value = Math.round(maxCount - (i * yStep));
                      const y = paddingTop + (i * (chartHeight / yTicks));
                      return (
                        <g key={i}>
                          <line
                            x1={paddingLeft}
                            y1={y}
                            x2={paddingLeft + chartWidth}
                            y2={y}
                            stroke="#e0e0e0"
                            strokeWidth="1"
                            strokeDasharray="4,4"
                          />
                          <text
                            x={paddingLeft - 10}
                            y={y + 4}
                            fontSize="11"
                            textAnchor="end"
                            fill="#666"
                          >
                            {value}
                          </text>
                        </g>
                      );
                    })}

                    {/* X-axis line */}
                    <line
                      x1={paddingLeft}
                      y1={paddingTop + chartHeight}
                      x2={paddingLeft + chartWidth}
                      y2={paddingTop + chartHeight}
                      stroke="#666"
                      strokeWidth="2"
                    />

                    {/* Y-axis line */}
                    <line
                      x1={paddingLeft}
                      y1={paddingTop}
                      x2={paddingLeft}
                      y2={paddingTop + chartHeight}
                      stroke="#666"
                      strokeWidth="2"
                    />

                    {/* Area fill */}
                    <path d={areaPath} fill="#1976d2" fillOpacity="0.15" />

                    {/* Line */}
                    <path d={linePath} fill="none" stroke="#1976d2" strokeWidth="3" />

                    {/* Data points and X-axis labels */}
                    {points.map((point, i) => {
                      const showLabel = i % xLabelInterval === 0 || i === points.length - 1;
                      const dateObj = new Date(point.data.date);
                      const dateLabel = `${dateObj.getMonth() + 1}/${dateObj.getDate()}`;

                      return (
                        <g key={i}>
                          <circle cx={point.x} cy={point.y} r="4" fill="#1976d2" />
                          {showLabel && (
                            <text
                              x={point.x}
                              y={paddingTop + chartHeight + 20}
                              fontSize="11"
                              textAnchor="middle"
                              fill="#666"
                            >
                              {dateLabel}
                            </text>
                          )}
                        </g>
                      );
                    })}

                    {/* Y-axis label */}
                    <text
                      x={15}
                      y={paddingTop + chartHeight / 2}
                      fontSize="12"
                      textAnchor="middle"
                      fill="#666"
                      transform={`rotate(-90, 15, ${paddingTop + chartHeight / 2})`}
                      fontWeight="600"
                    >
                      Patients
                    </text>

                    {/* X-axis label */}
                    <text
                      x={paddingLeft + chartWidth / 2}
                      y={height - 10}
                      fontSize="12"
                      textAnchor="middle"
                      fill="#666"
                      fontWeight="600"
                    >
                      Date
                    </text>
                  </>
                );
              })()}
            </svg>
          </Box>
        </ChartContainer>
      </Box>

      {/* Row 3: Stage Distribution & Conversion Trend */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <ChartContainer
            title="Current Stage Distribution"
            subtitle="Active patients by workflow stage"
            height={280}
          >
            <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
              <svg width="100%" height="220" viewBox="0 0 450 220" style={{ overflow: 'visible' }}>
                {(() => {
                  const maxCount = Math.max(...stageDistribution.map(s => s.count), 1);
                  const width = 450;
                  const height = 220;
                  const paddingLeft = 50;
                  const paddingRight = 20;
                  const paddingTop = 20;
                  const paddingBottom = 40;
                  const chartWidth = width - paddingLeft - paddingRight;
                  const chartHeight = height - paddingTop - paddingBottom;
                  const barWidth = chartWidth / stageDistribution.length - 20;
                  const barSpacing = chartWidth / stageDistribution.length;

                  // Y-axis ticks
                  const yTicks = 4;
                  const yStep = maxCount / yTicks;

                  return (
                    <>
                      {/* Y-axis gridlines and labels */}
                      {Array.from({ length: yTicks + 1 }).map((_, i) => {
                        const value = Math.round(maxCount - (i * yStep));
                        const y = paddingTop + (i * (chartHeight / yTicks));
                        return (
                          <g key={i}>
                            <line
                              x1={paddingLeft}
                              y1={y}
                              x2={paddingLeft + chartWidth}
                              y2={y}
                              stroke="#e0e0e0"
                              strokeWidth="1"
                              strokeDasharray="4,4"
                            />
                            <text
                              x={paddingLeft - 10}
                              y={y + 4}
                              fontSize="11"
                              textAnchor="end"
                              fill="#666"
                            >
                              {value}
                            </text>
                          </g>
                        );
                      })}

                      {/* X-axis line */}
                      <line
                        x1={paddingLeft}
                        y1={paddingTop + chartHeight}
                        x2={paddingLeft + chartWidth}
                        y2={paddingTop + chartHeight}
                        stroke="#666"
                        strokeWidth="2"
                      />

                      {/* Y-axis line */}
                      <line
                        x1={paddingLeft}
                        y1={paddingTop}
                        x2={paddingLeft}
                        y2={paddingTop + chartHeight}
                        stroke="#666"
                        strokeWidth="2"
                      />

                      {/* Bars */}
                      {stageDistribution.map((stage, i) => {
                        const barHeight = (stage.count / maxCount) * chartHeight;
                        const x = paddingLeft + (i * barSpacing) + 10;
                        const y = paddingTop + chartHeight - barHeight;

                        return (
                          <g key={i}>
                            {/* Bar */}
                            <rect
                              x={x}
                              y={y}
                              width={barWidth}
                              height={barHeight}
                              fill="#1976d2"
                              rx="4"
                            />
                            {/* Value label on top of bar */}
                            <text
                              x={x + barWidth / 2}
                              y={y - 8}
                              fontSize="12"
                              textAnchor="middle"
                              fill="#666"
                              fontWeight="600"
                            >
                              {stage.count}
                            </text>
                            {/* Stage label */}
                            <text
                              x={x + barWidth / 2}
                              y={paddingTop + chartHeight + 20}
                              fontSize="11"
                              textAnchor="middle"
                              fill="#666"
                            >
                              {stage.stage}
                            </text>
                          </g>
                        );
                      })}

                      {/* Y-axis label */}
                      <text
                        x={15}
                        y={paddingTop + chartHeight / 2}
                        fontSize="12"
                        textAnchor="middle"
                        fill="#666"
                        transform={`rotate(-90, 15, ${paddingTop + chartHeight / 2})`}
                        fontWeight="600"
                      >
                        Patients
                      </text>
                    </>
                  );
                })()}
              </svg>
            </Box>
          </ChartContainer>
        </Grid>
        <Grid item xs={12} md={6}>
          <ChartContainer
            title="Conversion Rate Trend"
            subtitle="Weekly conversion rate over time"
            height={280}
          >
            <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
              <svg width="100%" height="220" viewBox="0 0 450 220" style={{ overflow: 'visible' }}>
                {conversionTrendData.length > 1 && (() => {
                  const maxRate = 100; // percentage
                  const width = 450;
                  const height = 220;
                  const paddingLeft = 50;
                  const paddingRight = 20;
                  const paddingTop = 20;
                  const paddingBottom = 40;
                  const chartWidth = width - paddingLeft - paddingRight;
                  const chartHeight = height - paddingTop - paddingBottom;
                  const stepX = chartWidth / (conversionTrendData.length - 1);

                  // Y-axis ticks (0%, 25%, 50%, 75%, 100%)
                  const yTicks = [0, 25, 50, 75, 100];

                  // Create path for line
                  const points = conversionTrendData.map((d, i) => {
                    const x = paddingLeft + i * stepX;
                    const y = paddingTop + chartHeight - ((d.rate / maxRate) * chartHeight);
                    return { x, y, data: d };
                  });

                  const pathPoints = points.map(p => `${p.x},${p.y}`).join(' L ');
                  const linePath = `M ${pathPoints}`;

                  return (
                    <>
                      {/* Y-axis gridlines and labels */}
                      {yTicks.map((tick, i) => {
                        const y = paddingTop + chartHeight - ((tick / maxRate) * chartHeight);
                        return (
                          <g key={i}>
                            <line
                              x1={paddingLeft}
                              y1={y}
                              x2={paddingLeft + chartWidth}
                              y2={y}
                              stroke="#e0e0e0"
                              strokeWidth="1"
                              strokeDasharray="4,4"
                            />
                            <text
                              x={paddingLeft - 10}
                              y={y + 4}
                              fontSize="11"
                              textAnchor="end"
                              fill="#666"
                            >
                              {tick}%
                            </text>
                          </g>
                        );
                      })}

                      {/* X-axis line */}
                      <line
                        x1={paddingLeft}
                        y1={paddingTop + chartHeight}
                        x2={paddingLeft + chartWidth}
                        y2={paddingTop + chartHeight}
                        stroke="#666"
                        strokeWidth="2"
                      />

                      {/* Y-axis line */}
                      <line
                        x1={paddingLeft}
                        y1={paddingTop}
                        x2={paddingLeft}
                        y2={paddingTop + chartHeight}
                        stroke="#666"
                        strokeWidth="2"
                      />

                      {/* Line */}
                      <path d={linePath} fill="none" stroke="#2e7d32" strokeWidth="3" />

                      {/* Data points and X-axis labels */}
                      {points.map((point, i) => (
                        <g key={i}>
                          <circle cx={point.x} cy={point.y} r="4" fill="#2e7d32" />
                          <text
                            x={point.x}
                            y={paddingTop + chartHeight + 20}
                            fontSize="11"
                            textAnchor="middle"
                            fill="#666"
                          >
                            {point.data.week}
                          </text>
                        </g>
                      ))}

                      {/* Y-axis label */}
                      <text
                        x={15}
                        y={paddingTop + chartHeight / 2}
                        fontSize="12"
                        textAnchor="middle"
                        fill="#666"
                        transform={`rotate(-90, 15, ${paddingTop + chartHeight / 2})`}
                        fontWeight="600"
                      >
                        Conversion Rate (%)
                      </text>

                      {/* X-axis label */}
                      <text
                        x={paddingLeft + chartWidth / 2}
                        y={height - 5}
                        fontSize="12"
                        textAnchor="middle"
                        fill="#666"
                        fontWeight="600"
                      >
                        Week
                      </text>
                    </>
                  );
                })()}
              </svg>
            </Box>
          </ChartContainer>
        </Grid>
      </Grid>

      {/* Patient Details Table */}
      <AnalyticsPatientTable
        patients={filteredRecords}
        drillDownFilter={drillDownFilter}
        onClearFilter={() => setDrillDownFilter(null)}
      />

      {/* AI Insights - Fixed Bottom */}
      <AIInsights
        insights={aiInsights.insights}
        recommendations={aiInsights.recommendations}
        summary={aiInsights.summary}
        onExpandedChange={setAiInsightsExpanded}
      />
    </Box>
  );
};

export default ManagerDashboard;
